import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { admin } from '../../lib/api';
import type { Player, Position } from '../../types';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { ErrorMessage, Field, Label } from '../../ui/fieldset';
import { Heading } from '../../ui/heading';
import { Input } from '../../ui/input';
import { Text } from '../../ui/text';
import { EmptyState, Panel, StatCard } from '../../components/PageChrome';

const POSITIONS: Position[] = ['P', 'C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'DH', 'EH'];

const isValidJersey = (s: string) => /^\d{1,3}$/.test(s.trim());

const compareJersey = (a: Player, b: Player) => {
  const na = parseInt(a.jerseyNumber, 10);
  const nb = parseInt(b.jerseyNumber, 10);
  if (Number.isFinite(na) && Number.isFinite(nb) && na !== nb) return na - nb;
  return a.jerseyNumber.localeCompare(b.jerseyNumber);
};

interface FormState {
  firstName: string;
  lastName: string;
  jerseyNumber: string;
  positions: Position[];
}

const emptyForm = (): FormState => ({
  firstName: '',
  lastName: '',
  jerseyNumber: '',
  positions: [],
});

interface PlayerFormProps {
  initial: FormState;
  onSubmit: (form: FormState) => Promise<void>;
  onCancel: () => void;
  submitLabel: string;
}

function PlayerForm({ initial, onSubmit, onCancel, submitLabel }: PlayerFormProps) {
  const [form, setForm] = useState<FormState>(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  const togglePosition = (p: Position) => {
    setForm((f) => ({
      ...f,
      positions: f.positions.includes(p)
        ? f.positions.filter((x) => x !== p)
        : [...f.positions, p],
    }));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValidJersey(form.jerseyNumber)) {
      setError('Jersey must be 1–3 digits (e.g., 0, 00, 42).');
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      await onSubmit(form);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_1fr_5rem]">
        <Field>
          <Label>First name</Label>
          <Input
            required
            value={form.firstName}
            onChange={(e) => setForm({ ...form, firstName: e.target.value })}
          />
        </Field>
        <Field>
          <Label>Last name</Label>
          <Input
            required
            value={form.lastName}
            onChange={(e) => setForm({ ...form, lastName: e.target.value })}
          />
        </Field>
        <Field>
          <Label>Jersey #</Label>
          <Input
            required
            inputMode="numeric"
            pattern="\d{1,3}"
            maxLength={3}
            value={form.jerseyNumber}
            onChange={(e) =>
              setForm({ ...form, jerseyNumber: e.target.value.replace(/\D/g, '') })
            }
            className="text-center font-mono"
          />
        </Field>
      </div>

      <Field>
        <Label>Primary positions</Label>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {POSITIONS.map((p) => {
            const on = form.positions.includes(p);
            return (
              <button
                key={p}
                type="button"
                onClick={() => togglePosition(p)}
                className={
                  on
                    ? 'rounded bg-zinc-100 px-2 py-1 text-xs font-medium text-zinc-900'
                    : 'rounded border border-zinc-700 px-2 py-1 text-xs text-zinc-400 hover:text-zinc-100'
                }
              >
                {p}
              </button>
            );
          })}
        </div>
      </Field>

      {error ? <ErrorMessage>{error}</ErrorMessage> : null}

      <div className="flex gap-2">
        <Button type="submit" color="dark/zinc" disabled={busy}>
          {busy ? 'Saving…' : submitLabel}
        </Button>
        <Button type="button" outline onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

export default function Roster() {
  const { teamId } = useParams<{ teamId: string }>();

  const [players, setPlayers] = useState<Player[]>([]);
  const [error, setError] = useState<string>();
  const [showNew, setShowNew] = useState(false);

  const load = async () => {
    if (!teamId) return;
    try {
      const res = await admin.listPlayers(teamId);
      setPlayers([...res.players].sort(compareJersey));
    } catch (err) {
      setError(String(err));
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId]);

  const handleCreate = async (form: FormState) => {
    if (!teamId) return;
    await admin.createPlayer(teamId, {
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      jerseyNumber: form.jerseyNumber.trim(),
      primaryPositions: form.positions.length > 0 ? form.positions : undefined,
    });
    setShowNew(false);
    await load();
  };

  const toggleActive = async (p: Player) => {
    if (!teamId) return;
    try {
      await admin.updatePlayer(teamId, p.playerId, { active: !p.active });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const remove = async (p: Player) => {
    if (!teamId) return;
    if (!window.confirm(`Delete ${p.name}? This cannot be undone.`)) return;
    try {
      await admin.deletePlayer(teamId, p.playerId);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <Heading level={2}>Roster</Heading>
          <Text className="mt-1 text-sm">
            Keep jersey numbers, active status, and primary positions ready before lineup work.
          </Text>
        </div>
        <Button color="dark/zinc" onClick={() => setShowNew((v) => !v)}>
          {showNew ? 'Cancel' : 'Add player'}
        </Button>
      </div>

      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard label="Active" value={players.filter((p) => p.active).length} tone="emerald" />
        <StatCard label="Inactive" value={players.filter((p) => !p.active).length} />
        <StatCard label="Positions set" value={players.filter((p) => p.primaryPositions.length > 0).length} />
      </div>

      {showNew ? (
        <Panel>
          <PlayerForm
            initial={emptyForm()}
            onSubmit={handleCreate}
            onCancel={() => setShowNew(false)}
            submitLabel="Add player"
          />
        </Panel>
      ) : null}

      {error ? <Text className="text-red-400">{error}</Text> : null}

      {players.length === 0 ? (
        <EmptyState
          title="No players yet"
          body="Add the active roster now so lineup and scorekeeping can use player names on game day."
          action={<Button color="dark/zinc" onClick={() => setShowNew(true)}>Add player</Button>}
        />
      ) : (
        <ul className="grid gap-3 lg:grid-cols-2">
          {players.map((p) => (
            <li
              key={p.playerId}
              className={`rounded-lg border border-zinc-800 bg-zinc-900 p-4 ${p.active ? '' : 'opacity-70'}`}
            >
              <div className="flex items-start gap-4">
                <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-zinc-950 font-mono text-sm text-zinc-300">
                  {p.photoUrl ? (
                    <img src={p.photoUrl} alt="" className="size-full rounded-lg object-cover" />
                  ) : (
                    <>#{p.jerseyNumber}</>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="font-medium text-zinc-100">{p.name}</div>
                    <Badge color={p.active ? 'lime' : 'zinc'}>
                      {p.active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {p.primaryPositions.length > 0 ? (
                      p.primaryPositions.map((pos) => (
                        <Badge key={pos} color="zinc">{pos}</Badge>
                      ))
                    ) : (
                      <Text className="text-xs">No positions set</Text>
                    )}
                  </div>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap justify-end gap-2">
                <Button href={`/admin/teams/${teamId}/players/${p.playerId}`} plain>
                  Edit
                </Button>
                <Button plain onClick={() => void toggleActive(p)}>
                  {p.active ? 'Deactivate' : 'Activate'}
                </Button>
                <Button plain onClick={() => void remove(p)} className="text-red-400">
                  Delete
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
